import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with defaults
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      // Only redirect if not already on auth pages
      if (!window.location.pathname.includes('/auth') && window.location.pathname !== '/') {
        window.location.href = '/auth';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  googleSession: (sessionId) => api.post('/auth/google/session', { session_id: sessionId }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// Profile APIs
export const profileAPI = {
  create: (data) => api.post('/profile', data),
  get: () => api.get('/profile'),
  getById: (userId) => api.get(`/profile/${userId}`),
  update: (data) => api.put('/profile', data),
};

// Preferences APIs
export const preferencesAPI = {
  save: (data) => api.post('/preferences', data),
  get: () => api.get('/preferences'),
};

// Discovery APIs
export const discoveryAPI = {
  getProfiles: () => api.get('/discover'),
  swipe: (data) => api.post('/swipe', data),
  getMatches: () => api.get('/matches'),
  getLikesReceived: () => api.get('/likes-received'),
};

// Messages APIs
export const messagesAPI = {
  send: (data) => api.post('/messages', data),
  get: (matchId) => api.get(`/messages/${matchId}`),
};

// AI APIs
export const aiAPI = {
  getIcebreakers: (targetUserId) => api.get(`/ai/icebreakers/${targetUserId}`),
  getMatchSuggestions: () => api.get('/ai/match-suggestions'),
  getSuggestion: (targetUserId) => api.get(`/ai/suggestion/${targetUserId}`),
};

// Subscription APIs
export const subscriptionAPI = {
  getPlans: () => api.get('/subscription/plans'),
  createCheckout: (data) => api.post('/subscription/checkout', data),
  checkStatus: (sessionId) => api.get(`/subscription/status/${sessionId}`),
};

// Safety APIs
export const safetyAPI = {
  block: (userId) => api.post(`/block/${userId}`),
  unblock: (userId) => api.delete(`/block/${userId}`),
  getBlocked: () => api.get('/blocked'),
  report: (userId, data) => api.post(`/report/${userId}`, data),
};

// Verification APIs
export const verificationAPI = {
  submitSelfie: (selfieImage) => api.post('/verification/selfie', { selfie_image: selfieImage }),
  getStatus: () => api.get('/verification/status'),
};

// Partner APIs (Find a Workout Partner)
export const partnerAPI = {
  updateAvailability: (data) => api.post('/partner/availability', data),
  findPartners: () => api.get('/partner/find'),
  findNearbyPartners: (maxDistance = 25) => api.get(`/partner/find/nearby?max_distance=${maxDistance}`),
  sendRequest: (userId, data) => api.post(`/partner/request/${userId}`, data),
  getRequests: () => api.get('/partner/requests'),
  respondToRequest: (requestId, action) => api.post(`/partner/requests/${requestId}/respond`, { action }),
  getConnections: () => api.get('/partner/connections'),
};

// Location APIs
export const locationAPI = {
  updateLocation: (lat, lng) => api.post('/profile/location', { lat, lng }),
};

// Session APIs (Workout Sessions)
export const sessionAPI = {
  create: (data) => api.post('/sessions', data),
  getAll: () => api.get('/sessions'),
  update: (sessionId, data) => api.put(`/sessions/${sessionId}`, data),
  cancel: (sessionId) => api.delete(`/sessions/${sessionId}`),
};

// Notification APIs
export const notificationAPI = {
  getAll: () => api.get('/notifications'),
  markRead: (notificationId) => api.post(`/notifications/${notificationId}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

// Constants APIs
export const constantsAPI = {
  getSports: () => api.get('/constants/sports'),
  getLevels: () => api.get('/constants/levels'),
  getPrompts: () => api.get('/constants/prompts'),
};

export default api;
